import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Calendar,
  Users,
  IndianRupee,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Play,
  FileText,
  History,
  Search,
  Filter,
  RefreshCw,
  Info,
  Edit,
  ArrowRight,
  Lock,
  CheckCircle,
  FileCheck,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { getEmployees } from "@/services/adminService";
import {
  completePayRun,
  deletePayrollRun,
  getAllPayrollRuns,
  getandCreatePayrollData,
  getLatestPayrollRun,
  lockPayrollRun,
  savePayrollDraft,
} from "@/services/payroll.service";
import { PayrollSheet } from "@/components/admin/payroll/PayrollSheet";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { PayrollRunTable } from "./PayrollRunTable";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const RegularPayroll = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [payrollMonth, setPayrollMonth] = useState("");
  const [payrollYear, setPayrollYear] = useState("");
  // The period that was just completed. Captured before fetchPayrollRuns advances
  // payrollMonth/Year to the next month, so the completion card + "View Payslips"
  // point at the run the user actually finished.
  const [completedPeriod, setCompletedPeriod] = useState({ month: "", year: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [payrollRunId, setPayrollRunId] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const [showPayrollTable, setShowPayrollTable] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payrollStatus, setPayrollStatus] = useState("draft");

  const [employeesLength, setEmployeeLength] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });
  const [showNoEmployeeDialog, setShowNoEmployeeDialog] = useState(false);
  const { canAddEmployees } = useOnboarding();
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);

  const navigate = useNavigate();

  useEffect(() => {
    const now = new Date();
    setPayrollMonth(String(now.getMonth() + 1).padStart(2, "0"));
    setPayrollYear(String(now.getFullYear()));
    fetchPayrollRuns();
  }, []);

  // Run auto-select ONLY the first time payroll table is shown
  useEffect(() => {
    if (
      showPayrollTable &&
      selectedEmployees.length === 0 &&
      employees.length > 0
    ) {
      setSelectedEmployees(employees.map((emp) => emp.employee_id));
    }
  }, [showPayrollTable, employees]);

  const fetchPayrollRuns = async () => {
    try {
      const response = await getAllPayrollRuns();
      setPayrollHistory(response.data);
      setEmployeeLength(response.employeesLength);
      if (response.data.length > 0) {
        const newResponse = await getLatestPayrollRun();

        if (newResponse.status === "draft" || newResponse.status === "locked") {
          toast.info("Loaded your existing draft payroll run.");
          // Set selected period
          setPayrollMonth(newResponse.period_month);
          setPayrollYear(newResponse.period_year);
          setPayrollStatus(newResponse.status);
          setEmployees(newResponse.employees);
          setShowPayrollTable(true);
          setPayrollRunId(newResponse._id);
          return;
        }

        if (newResponse.status === "completed") {
          // Move to next period
          const next = getNextMonth(
            newResponse.period_month,
            newResponse.period_year
          );
          setPayrollMonth(next.month);
          setPayrollYear(next.year);

          toast.info(
            "You have completed last month’s payroll — calculate for the new month."
          );
          // Hide table until user runs calculation
          setShowPayrollTable(false);

          return;
        }
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
  const currentYear = String(now.getFullYear());

  const getMonthName = (monthNum) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNum - 1];
  };

  const getPrevMonth = (month, year) => {
    let m = parseInt(month);
    let y = parseInt(year);
    m = m === 1 ? 12 : m - 1;
    y = m === 12 ? y - 1 : y;
    return { month: String(m).padStart(2, "0"), year: String(y) };
  };
  const getNextMonth = (month, year) => {
    let m = parseInt(month);
    let y = parseInt(year);
    m = m === 12 ? 1 : m + 1;
    y = m === 1 ? y + 1 : y;
    return { month: String(m).padStart(2, "0"), year: String(y) };
  };

  const prevMonthData = getPrevMonth(currentMonth, currentYear);
  const nextMonthData = getNextMonth(currentMonth, currentYear);

  const handleLockAndProcess = async () => {
    if (!canEdit("run_payroll", true)) return;
    setShowConfirmDialog(true);
  };

  const handleCompletePayRun = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      setProcessing(true);

      const response = await completePayRun(payrollRunId);

      // Remember the completed period NOW — fetchPayrollRuns() below advances
      // payrollMonth/Year to the next month.
      setCompletedPeriod({ month: payrollMonth, year: payrollYear });

      toast.success(
        "Payroll Run Completed and generated Payslips Successfully!"
      );
      setPayrollStatus("completed");
      setProcessing(false);
      fetchPayrollRuns();
    } catch (err) {
      toast.error("Failed to Record Disbursement!");
      setProcessing(false);
    }
  };

  const calculateSelectedNet = () => {
    return employees
      .filter((emp) => selectedEmployees.includes(emp.employee_id))
      .reduce((sum, emp) => sum + Number(emp.net_pay || 0), 0);
  };

  const handleOverwritePayroll = async () => {
    setShowDuplicateDialog(false);
    setShowConfirmDialog(true);
  };

  const handleConfirmLock = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      setProcessing(true);

      const selectedEmployeePayload = employees.filter((emp) =>
        selectedEmployees.includes(emp.employee_id)
      );

      const response = await lockPayrollRun(
        payrollRunId,
        selectedEmployeePayload
      );

      toast.success("Payroll Run Locked Successfully!");

      // Keep only selected employees visible after lock
      setEmployees((prev) =>
        prev.filter((emp) => selectedEmployees.includes(emp.employee_id))
      );

      // Also update selected list to avoid mismatch
      setSelectedEmployees((prev) =>
        prev.filter((id) => selectedEmployees.includes(id))
      );

      setPayrollStatus("locked");
      setProcessing(false);
    } catch (err) {
      toast.error("Failed to Confirm and Lock!");
      setProcessing(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        toast.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportExcel = async () => {
    if (!canEdit("run_payroll", true)) return;
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }
  };

  const resetImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportResults(null);
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment =
      departmentFilter === "all" || emp.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  const visibleEmployees = employees.filter((emp) =>
    selectedEmployees.includes(emp.employee_id)
  );

  const totals = {
    totalEmployees: visibleEmployees.length,
    totalGross: visibleEmployees.reduce(
      (sum, e) => sum + (e.gross_pay || 0),
      0
    ),
    totalDeductions: visibleEmployees.reduce(
      (sum, e) => sum + (e.total_deductions || 0),
      0
    ),
    totalNet: visibleEmployees.reduce((sum, e) => sum + (e.net_pay || 0), 0),
  };

  const departments = [
    ...new Set(employees.map((emp) => emp.department)),
  ].filter(Boolean);
  const monthName = new Date(payrollYear, payrollMonth - 1).toLocaleString(
    "default",
    { month: "long" }
  );

  // A later regular payroll (any status) that would be left inconsistent if we
  // delete this earlier period — deleting reverses this run's loan EMIs and
  // reimbursements underneath a payroll that was already built on top of them.
  // The earliest such run is what the admin must handle first.
  const runOrdinal = (m, y) => Number(y) * 12 + Number(m);
  const laterRegularRun =
    payrollMonth && payrollYear
      ? payrollHistory
          .filter(
            (r) =>
              r.type === "regular" &&
              runOrdinal(r.period_month, r.period_year) >
                runOrdinal(payrollMonth, payrollYear),
          )
          .sort(
            (a, b) =>
              runOrdinal(a.period_month, a.period_year) -
              runOrdinal(b.period_month, b.period_year),
          )[0] || null
      : null;
  const laterRunLabel = laterRegularRun
    ? `${getMonthName(Number(laterRegularRun.period_month))} ${laterRegularRun.period_year}`
    : "";
  const laterRunInProgress =
    laterRegularRun && laterRegularRun.status !== "completed";

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleRunPayroll = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      const employees = await getEmployees();

      const activeEmployees = employees.filter(
        (emp) =>
          emp.status === "active" &&
          emp.salary_structure &&
          emp.salary_structure?.length > 0
      );

      // Case 1 — No active employees at all
      if (activeEmployees.length === 0) {
        setShowNoEmployeeDialog(true);
        return;
      }

      // Case 2 — Pending employees (active but missing salary structure)
      const isPending = employees.filter(
        (emp) =>
          emp.status === "active" &&
          (!emp.salary_structure || emp.salary_structure.length === 0)
      );

      if (isPending.length > 0) {
        setConfirmOpen(true);

        const title = "Pending Employees Detected";
        const message = `There are ${isPending.length} pending employees, these will not be included in Payroll. You wish to continue?`;
        setConfirmData({ title, message });
        return;
      }

      const body = {
        month: payrollMonth,
        year: payrollYear,
      };
      const response = await getandCreatePayrollData(body);
      setEmployees(response.data);
      setPayrollRunId(response.payrollRunId);
      setShowPayrollTable(true);
      setSelectedEmployees(response.data.map((emp) => emp.employee_id)); // ✅ Auto-select all
    } catch (err) {
      toast.error("Error Calculating Payroll");
    }
  };

  const handleCreateAndGetPayroll = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      const body = {
        month: payrollMonth,
        year: payrollYear,
      };
      const response = await getandCreatePayrollData(body);
      setEmployees(response.data);
      setPayrollRunId(response.payrollRunId);
      setShowPayrollTable(true);
      setSelectedEmployees(response.data.map((emp) => emp.employee_id)); // ✅ Auto-select all
    } catch (err) {
      toast.error("Error Calculating Payroll");
    }
  };

  const handlePayrollUpdate = async (updatedEmployee) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.employee_id === updatedEmployee.employee_id ? updatedEmployee : emp
      )
    );

    // Persist the edit so it survives a page refresh (a completed run is frozen).
    if (payrollStatus === "completed" || !payrollRunId) return;
    try {
      await savePayrollDraft(payrollRunId, [updatedEmployee]);
      toast.success("Changes saved");
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  const activeStyle =
    "border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-transparent";
  const inactiveStyle =
    "border-dashed border-gray-400 hover:border-blue-500 dark:border-gray-600 hover:bg-transparent";
  const baseButtonClasses =
    "flex flex-col items-center text-foreground justify-center bg-transparent border border-outline p-4 h-[120px] w-[150px] border-2 rounded-xl relative cursor-pointer";
  const isSelected = (month, year) =>
    payrollMonth === month && payrollYear === year;

  const getStatusClasses = (status) => {
    switch (status) {
      case "draft":
        return {
          text: "text-gray-700 dark:text-gray-300",
          bg: "bg-gray-100 dark:bg-gray-700",
          dot: "bg-gray-400",
        };
      case "locked":
        return {
          text: "text-amber-700",
          bg: "bg-amber-100",
          dot: "bg-amber-500",
        };
      case "completed":
        return {
          text: "text-emerald-700",
          bg: "bg-emerald-100",
          dot: "bg-emerald-500",
        };
      case "failed":
        return {
          text: "text-red-700",
          bg: "bg-red-100",
          dot: "bg-red-500",
        };
      default:
        return getStatusClasses("draft");
    }
  };

  const statusClasses = getStatusClasses(payrollStatus || "draft");
  const Placeholder = ({ count, className = "" }) => (
    <span
      className={`inline-block h-6 rounded bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`}
      style={{ width: `${count * 15}px` }}
    ></span>
  );

  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map((emp) => emp.employee_id));
    }
  };

  const handleDeletePayrollRun = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
      await deletePayrollRun(payrollRunId);

      toast.success("Payroll run deleted successfully");

      // 🔥 FULL STATE RESET (IMPORTANT)
      setPayrollRunId(null);
      setPayrollStatus("draft");
      setEmployees([]);
      setSelectedEmployees([]);
      setShowPayrollTable(false);

      // Optional safety resets
      setSearchTerm("");
      setDepartmentFilter("all");

      // Reload history cleanly
      await fetchPayrollRuns();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete payroll run",
      );
    }
  };

  const blockIfNotOnboarded = () => {
    if (!canAddEmployees) {
      toast.warning("Complete onboarding first to manage employees.");
      navigate("/dashboard");
      return true;
    }
    return false;
  };
  const showDaysWorked = filteredEmployees.some(
    (emp) =>
      emp.total_unpaid_leaves !== null && emp.total_unpaid_leaves !== undefined
  );

  return (
    <div className=" space-y-6 min-h-screen">
      {/* 🛑 No Employees Created */}
      {employeesLength === 0 && (
        <Card className="mt-6 text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-700">
          <CardContent className="flex justify-center items-center flex-col">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />

            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
              No Active Employees Found
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              You have not added any employees yet. Add employees first to
              generate payroll for the month.
            </p>

            <Button
              size="lg"
              className="flex items-center gap-2 w-[190px]"
              onClick={() => {
                if (blockIfNotOnboarded()) return;
                navigate("/employees/new");
              }}
            >
              <Users className="w-4 h-4" />
              Add Employee
            </Button>
          </CardContent>
        </Card>
      )}

      {employeesLength > 0 && (
        <>
          {/* Period Selection */}
          <Card
            className="rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.06)] 
        border border-gray-200 dark:border-gray-700 
        bg-white dark:bg-gray-900 
        transition-all duration-300"
          >
            <CardContent className="py-6 px-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                {/* LEFT: Payroll Period */}
                <div className="md:col-span-2 flex items-center space-x-4">
                  <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-primary/30">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>

                  <div>
                    <Label
                      className="text-xs font-semibold uppercase tracking-wide 
            text-gray-500 dark:text-gray-400"
                    >
                      Payroll Period
                    </Label>

                    <p className="text-3xl font-black text-gray-900 dark:text-gray-100 mt-1">
                      {monthName} {payrollYear}
                    </p>
                  </div>
                </div>

                {/* RIGHT: Status Chip */}
                <div className="md:col-span-2 flex w-full md:justify-end">
                  <span
                    className={`
      inline-flex items-center gap-2
      px-4 py-2 rounded-full 
      text-sm font-semibold tracking-wide
      border border-transparent capitalize
      ${statusClasses.bg} ${statusClasses.text}
      shadow-sm transition-all duration-300
      ml-auto
    `}
                  >
                    <span
                      className={`
        w-2.5 h-2.5 rounded-full ${statusClasses.dot}
        shadow-[0_0_6px_currentColor]
      `}
                    ></span>

                    {payrollStatus || "draft"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative">
            {/* Overlay for subtle message when data is hidden */}
            {/* 1️⃣ Overlay when calculation is NOT run */}
            {!showPayrollTable && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center 
      bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg 
      pointer-events-none transition-opacity duration-300"
              >
                <p className="text-md font-medium text-gray-700 dark:text-gray-300">
                  Run Calculation to view totals
                </p>
              </div>
            )}

            {/* 2️⃣ Overlay when table is shown BUT no employees selected */}
            {showPayrollTable && selectedEmployees.length === 0 && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center 
      bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg 
      pointer-events-none transition-opacity duration-300"
              >
                <p className="text-md font-medium text-gray-700 dark:text-gray-300">
                  Select employees to view totals
                </p>
              </div>
            )}

            {/* Card 1: Employees */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Employees
                    </p>
                    {!showPayrollTable ? (
                      <Placeholder count={3} className="mt-1" />
                    ) : (
                      <p className="text-2xl font-bold">
                        {totals.totalEmployees}
                      </p>
                    )}
                  </div>
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-500" />
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Gross Salary */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total Gross Salary
                    </p>
                    {!showPayrollTable ? (
                      <Placeholder count={5} className="mt-1" />
                    ) : (
                      <p className="text-2xl font-bold text-primary dark:text-emerald-500">
                        {formatCurrency(totals.totalGross)}
                      </p>
                    )}
                  </div>
                  <TrendingUp className="w-8 h-8 text-primary dark:text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Deductions */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total Deductions
                    </p>
                    {!showPayrollTable ? (
                      <Placeholder count={4} className="mt-1" />
                    ) : (
                      <p className="text-2xl font-bold text-red-600 dark:text-red-500">
                        {formatCurrency(totals.totalDeductions)}
                      </p>
                    )}
                  </div>
                  <IndianRupee className="w-8 h-8 text-red-600 dark:text-red-500" />
                </div>
              </CardContent>
            </Card>

            {/* Card 4: Net Payable (Highlight) */}
            <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                      Total Net Payable
                    </p>
                    {!showPayrollTable ? (
                      <Placeholder count={6} className="mt-1" />
                    ) : (
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                        {formatCurrency(totals.totalNet)}
                      </p>
                    )}
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {/* ---------------------- Step 1: Run Payroll Button (initial view) ---------------------- */}
            {!showPayrollTable &&
              payrollStatus !== "completed" &&
              payrollHistory.filter((r) => r.type === "regular").length > 0 && (
                <Card className="mt-6 text-center py-10 border-2 border-dashed border-gray-300 dark:border-gray-700">
                  <CardContent>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                      Ready to calculate payroll for this month?
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">
                      Click below to generate gross pay, deductions, and net pay
                      for all active employees.
                    </p>
                    <Button onClick={() => handleRunPayroll()}>
                      <Play className="w-5 h-5 mr-2" />
                      Run Payroll Calculation
                    </Button>
                  </CardContent>
                </Card>
              )}
            {!showPayrollTable &&
              payrollStatus !== "completed" &&
              payrollHistory.filter((r) => r.type === "regular").length === 0 && (
                <Card className="mt-6 text-center py-10 border-2 border-dashed border-gray-300 dark:border-gray-700">
                  <CardContent>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                      <Info className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                      No Previous Payroll Runs Found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                      This looks like your first payroll run! Please select the
                      period you wish to generate payroll for before proceeding
                      to the calculation table.
                    </p>
                    <div className="flex justify-center space-x-6 p-6 bg-gray-50 dark:bg-gray-900 rounded-xl">
                      {/* Previous Month */}
                      <Button
                        className={`${baseButtonClasses} 
                  ${
                    isSelected(prevMonthData.month, prevMonthData.year)
                      ? activeStyle
                      : inactiveStyle
                  }`}
                        onClick={() => {
                          setPayrollMonth(prevMonthData.month);
                          setPayrollYear(prevMonthData.year);
                        }}
                      >
                        <History
                          // Icon color is handled here, using dark:text-blue-400 for inactive
                          className={`w-6 h-6 mb-1 `}
                        />
                        <span className="text-xs uppercase font-medium mt-1">
                          Previous
                        </span>
                        <span className="font-extrabold text-xl leading-none mt-0.5">
                          {getMonthName(prevMonthData.month)}
                        </span>
                        <span className="text-sm opacity-80">
                          {prevMonthData.year}
                        </span>
                      </Button>

                      {/* Current Month (Default) */}
                      <Button
                        className={`${baseButtonClasses}
                  ${
                    isSelected(currentMonth, currentYear)
                      ? activeStyle
                      : inactiveStyle
                  }`}
                        onClick={() => {
                          setPayrollMonth(currentMonth);
                          setPayrollYear(currentYear);
                        }}
                      >
                        <Calendar className={`w-6 h-6 mb-1`} />
                        <span className="text-xs uppercase font-medium mt-1">
                          Current
                        </span>
                        <span className="font-extrabold text-xl leading-none mt-0.5">
                          {getMonthName(currentMonth)}
                        </span>
                        <span className="text-sm opacity-80">
                          {currentYear}
                        </span>
                      </Button>

                      {/* Next Month */}
                      <Button
                        className={`${baseButtonClasses}
                  ${
                    isSelected(nextMonthData.month, nextMonthData.year)
                      ? activeStyle
                      : inactiveStyle
                  }`}
                        onClick={() => {
                          setPayrollMonth(nextMonthData.month);
                          setPayrollYear(nextMonthData.year);
                        }}
                      >
                        <ArrowRight className={`w-6 h-6 mb-1`} />
                        <span className="text-xs uppercase font-medium mt-1">
                          Next
                        </span>
                        <span className="font-extrabold text-xl leading-none mt-0.5">
                          {getMonthName(nextMonthData.month)}
                        </span>
                        <span className="text-sm opacity-80">
                          {nextMonthData.year}
                        </span>
                      </Button>
                    </div>

                    <div className="flex flex-col items-center mt-6">
                      {/* Primary Action Button (Strong Emphasis) */}
                      <Button
                        onClick={() => handleRunPayroll()}
                        className="
          px-8 py-3 text-lg font-semibold text-white
          shadow-xl shadow-primary/50 
          transition-all duration-300 
          w-full md:w-auto 
        "
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Run Payroll Calculation
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            {/* ---------------------- Step 2: Search + Table shown together ---------------------- */}
            {showPayrollTable && payrollStatus !== "completed" && (
              <div className="space-y-6 animate-fade-in">
                {/* Search + Filter Card */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Search Field */}
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search by name or employee ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>

                      {/* Department Filter */}
                      <div className="w-full md:w-64">
                        <Select
                          value={departmentFilter}
                          onValueChange={setDepartmentFilter}
                        >
                          <SelectTrigger>
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="All Departments" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            {departments.map((dept) => (
                              <SelectItem key={dept} value={dept}>
                                {dept}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Select All Button */}
                      {payrollStatus !== "locked" && (
                        <Button variant="outline" onClick={handleSelectAll}>
                          {selectedEmployees.length === employees.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}

                      <Button
                        variant="destructive"
                        disabled={!canEdit("run_payroll")}
                        onClick={() => setDeleteConfirmOpen(true)}
                      >
                        Delete Payroll
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <PayrollRunTable
                  employees={filteredEmployees}
                  selectedEmployees={selectedEmployees}
                  setSelectedEmployees={setSelectedEmployees}
                  payrollStatus={payrollStatus}
                  showDaysWorked={true}
                  onLock={handleLockAndProcess}
                  onComplete={handleCompletePayRun}
                  onEditEmployee={(emp) => {
                    if (!canEdit("run_payroll", true)) return;
                    setSelectedEmployee(emp);
                    setOpenSheet(true);
                  }}
                />
              </div>
            )}
            {/* /* ---------------------- 🎉 Show Completion Summary---------------------- */}

            {payrollStatus === "completed" && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircle2 className="w-6 h-6 text-primary mr-2" />
                    Payroll Processed Successfully!
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-400">
                          Employees Processed
                        </p>
                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                          {totals.totalEmployees}
                        </p>
                      </div>

                      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                        <p className="text-sm text-emerald-700 dark:text-emerald-400">
                          Total Gross
                        </p>
                        <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-300">
                          {formatCurrency(totals.totalGross)}
                        </p>
                      </div>

                      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          Total Bonus
                        </p>
                        <p className="text-2xl font-bold text-amber-900 dark:text-amber-300">
                          ₹0
                        </p>
                      </div>

                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                        <p className="text-sm text-purple-700 dark:text-purple-400">
                          Net Payable
                        </p>
                        <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                          {formatCurrency(totals.totalNet)}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-center space-x-4">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() =>
                          navigate(
                            `/payslips?month=${encodeURIComponent(
                              completedPeriod.month,
                            )}&year=${encodeURIComponent(completedPeriod.year)}`,
                          )
                        }
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        View Payslips
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
      {/* Dialogs */}
      <AlertDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span>Payroll Already Exists</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Payroll for{" "}
              <strong>
                {monthName} {payrollYear}
              </strong>{" "}
              already exists.
              <br />
              <br />
              Do you want to <strong>overwrite</strong> the existing payroll?
              <br />
              <br />
              <span className="text-red-600">
                Warning: This will delete the previous payroll run and all
                associated payslips for this month.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleOverwritePayroll}
              className="bg-red-600 hover:bg-red-700"
            >
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="text-foreground">Confirm Lock Payroll</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to Lock Payroll for{" "}
              <strong>{selectedEmployees.length} employees</strong> for{" "}
              <strong>
                {monthName} {payrollYear}
              </strong>
              .
              <br />
              <br />
              Total net amount to be disbursed:{" "}
              <strong>{formatCurrency(calculateSelectedNet())}</strong>
              <br />
              <br />
              This action will permanently lock the payroll and disable all
              future editing. Are you sure you want to continue?{" "}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLock}>
              Confirm and Lock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={resetImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Payroll from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!importResults ? (
              <>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Upload your payroll Excel file (.xlsx or .xls)
                  </p>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="max-w-md mx-auto"
                  />
                  {importFile && (
                    <p className="text-sm text-green-600 mt-2">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
                    Instructions:
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                    <li>
                      • Download the template first if you haven't already
                    </li>
                    <li>
                      • Ensure Employee IDs match existing employees in the
                      system
                    </li>
                    <li>
                      • Month/Year format should be MM/YYYY (e.g., 10/2025)
                    </li>
                    <li>• All amounts should be numeric values</li>
                    <li>
                      • Importing will create or update payslips for the
                      specified month
                    </li>
                  </ul>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetImportDialog}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImportExcel}
                    disabled={!importFile || importing}
                  >
                    {importing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Import Payroll
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <div
                    className={`p-4 rounded-lg ${
                      importResults.error_count === 0
                        ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30"
                        : "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">Import Results</h4>
                    <div className="space-y-1 text-sm">
                      <p className="text-green-700 dark:text-green-400">
                        ✓ Successfully imported: {importResults.imported_count}{" "}
                        payslips
                      </p>
                      {importResults.error_count > 0 && (
                        <p className="text-red-700 dark:text-red-400">
                          ✗ Errors: {importResults.error_count}
                        </p>
                      )}
                    </div>
                  </div>

                  {importResults.errors && importResults.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2">
                        Errors:
                      </h4>
                      <ul className="text-sm text-red-800 dark:text-red-400 space-y-1">
                        {importResults.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button onClick={resetImportDialog}>Close</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <PayrollSheet
        openSheet={openSheet}
        setOpenSheet={setOpenSheet}
        selectedEmployee={selectedEmployee}
        onSavePayroll={handlePayrollUpdate}
        periodMonth={payrollMonth}
        periodYear={payrollYear}
      />

      <AlertDialog
        open={showNoEmployeeDialog}
        onOpenChange={setShowNoEmployeeDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No Active Employees Found</AlertDialogTitle>
            <AlertDialogDescription>
              No Active Employees Available <br />
              All existing employees are either incomplete, resigned, or
              terminated.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowNoEmployeeDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={handleCreateAndGetPayroll}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Payroll Run?"
        message={
          laterRegularRun ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300">
              Payroll for <strong>{laterRunLabel}</strong> is already{" "}
              {laterRunInProgress ? "in progress" : "completed"}. Deleting{" "}
              {monthName} {payrollYear} would leave it inconsistent (its loan EMIs
              and reimbursements were carried into the later run).
              <br />
              Delete the <strong>{laterRunLabel}</strong> payroll first.
            </div>
          ) : (
            `This will permanently delete payroll for ${monthName} ${payrollYear}.`
          )
        }
        description="All payslips and associated payroll data for this period will be permanently removed. This action cannot be undone."
        confirmText="Delete Payroll"
        cancelText="Cancel"
        destructive
        confirmDisabled={!!laterRegularRun}
        onConfirm={async () => {
          await handleDeletePayrollRun();
          setDeleteConfirmOpen(false);
        }}
      />
    </div>
  );
};

export default RegularPayroll;
